import { useEffect, useState } from 'react';
import { CustomReportAlumniRow } from './CustomWatchlistReportService';
import { STAGE_BADGE_STYLE, STAGE_LABELS_CUSTOM } from './customStage';
import { allowPerformAction } from '../../../library/Core/SmartFunctions';

const toneForValue = (value: string | null): string => {
    switch ((value ?? '').toUpperCase()) {
        case 'HIGH': return 'bg-success text-success';
        case 'MODERATE': return 'bg-info text-info';
        case 'LOW': return 'bg-danger text-danger';
        default: return 'bg-secondary text-secondary';
    }
};

const SignalChip = ({ title, value }: { title: string; value: string | null }) => (
    <span
    className={`badge d-flex align-items-center justify-content-center ${toneForValue(value)} bg-opacity-10 font-10 me-1`}
    style={{
        height: '20px',
        width: '20px'
    }}
    title={value ? `${title}: ${value}` : title}>
        {value ? value.charAt(0).toUpperCase() : '—'}
    </span>
);

/** Format API date (yyyy-mm-dd) as dd-mmm-yyyy (e.g. 03-Aug-2026). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatAddedOn = (iso: string | null): string => {
    if (!iso) return '—';
    const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const month = MONTHS[Number(m[2]) - 1];
    if (!month) return iso;
    return `${m[3]}-${month}-${m[1]}`;
};

const StageBadge = ({ row }: { row: CustomReportAlumniRow }) => {
    const code = row.stage || 'identified';
    const label = row.stage_label || STAGE_LABELS_CUSTOM[code] || code;
    const style = STAGE_BADGE_STYLE[code] || STAGE_BADGE_STYLE.identified;
    return (
        <span className="d-inline-flex align-items-center gap-1 text-nowrap">
            <span className="badge rounded-pill font-11 font-600 px-2" style={style}>
                {label}
            </span>
            {row.is_system_stage ? (
                <span className="stage-sysdriven font-10 text-muted">system</span>
            ) : null}
        </span>
    );
};

const CustomWatchlistReportTable = ({
    rows,
    onUpdateStage,
    onRemove,
}: {
    rows: CustomReportAlumniRow[];
    onUpdateStage: (row: CustomReportAlumniRow) => void;
    onRemove: (row: CustomReportAlumniRow) => void;
}) => {
    const canMutate = allowPerformAction();
    const [openId, setOpenId] = useState<string | null>(null);

    useEffect(() => {
        if (!openId) return;
        const close = () => setOpenId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openId]);

    return (
        <div className="table-responsive bg-white border rounded-3 shadow-sm" style={{ minHeight: 400 }}>
            <table className="table table-hover align-middle mb-0">
                <thead className="table-light text-muted font-11 text-uppercase">
                    <tr>
                        <th className="px-3 py-2">Alumni</th>
                        <th className="px-3 py-2">Program / Batch</th>
                        <th className="px-3 py-2">Company</th>
                        <th className="px-3 py-2">Designation</th>
                        <th className="px-3 py-2">Stage</th>
                        <th className="px-3 py-2">AP / MQ / VL</th>
                        <th className="px-3 py-2">Added On</th>
                        <th className="px-3 py-2 text-end">Actions</th>
                    </tr>
                </thead>
                <tbody className="font-13">
                    {rows.length === 0 ? (
                        <tr><td className="px-3 py-4 text-muted" colSpan={8}>No alumni match the current filters.</td></tr>
                    ) : rows.map((row) => (
                        <tr key={row.student_id}>
                            <td className="px-3 py-2">
                                <div className="font-600 text-dark-blue">
                                    <a
                                        href={`/alumni/${row.student_id}/profile`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-600 text-dark-blue text-decoration-none d-inline-block">
                                        {row.name ?? '—'}
                                    </a>
                                </div>
                            </td>
                            <td className="px-3 py-2"><div className="text-muted">{row.program_and_year || '—'}</div></td>
                            <td className="px-3 py-2"><div className="text-muted font-12">{row.company_name ?? '—'}</div></td>
                            <td className="px-3 py-2"><div className="text-muted font-12">{row.designation ?? '—'}</div></td>
                            <td className="px-3 py-2"><StageBadge row={row} /></td>
                            <td className="px-3 py-2 text-nowrap">
                                <div className="d-flex gap-1 align-items-center">
                                    <SignalChip title="Alma Mater Pride" value={row.alma_mater_pride} />
                                    <SignalChip title="Marketability" value={row.marketability} />
                                    <SignalChip title="Vibrancy" value={row.vibrancy} />
                                </div>
                            </td>
                            <td className="px-3 py-2 text-muted font-12 text-nowrap">{formatAddedOn(row.date_added)}</td>
                            <td className="px-3 py-2 text-end position-relative">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border rounded-3 font-12"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenId(openId === row.student_id ? null : row.student_id);
                                    }}>
                                    Action ▾
                                </button>
                                {openId === row.student_id && (
                                    <div
                                        className="position-absolute end-0 bg-white border rounded-3 shadow-sm py-1 smart-actions"
                                        style={{ zIndex: 10, minWidth: 200 }}
                                        onClick={(e) => e.stopPropagation()}>
                                        {canMutate && (
                                            <div
                                                className="px-3 py-2 font-12 cursor-pointer text-start hover-bg-light"
                                                onClick={() => { setOpenId(null); onUpdateStage(row); }}>
                                                Update Stage
                                            </div>
                                        )}
                                        <div
                                            className="px-3 py-2 font-12 cursor-pointer text-start hover-bg-light"
                                            onClick={() => {
                                                setOpenId(null);
                                                window.open(`/alumni/${row.student_id}/profile`, '_blank');
                                            }}>
                                            View Profile
                                        </div>
                                        {canMutate && (
                                            <>
                                                <hr className="my-1" />
                                                <div
                                                    className="px-3 py-2 font-12 cursor-pointer text-start text-danger hover-bg-light"
                                                    onClick={() => { setOpenId(null); onRemove(row); }}>
                                                    Remove from Watchlist
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default CustomWatchlistReportTable;
